import generator from 'generate-password-ts'
import { UserGrapheneType } from 'types/types'
import { computed } from 'vue'
import { VueLogger } from 'vue-logger-plugin'
import { Router } from 'vue-router'
import { useBrowserDetect } from 'vue3-detect-browser'

import {
  BACKEND_IMPERSONATE_STOP_URL,
  CSRF_COOKIE_NAME,
  CSRF_HEADER_NAME,
  ENV_DEVELOPMENT,
  ENV_PRODUCTION,
  ENV_STAGING,
  PUBLIC_USERNAME,
  ROOT_USERNAME,
  ROUTE_LOGIN,
  ROUTE_PRACTICE,
  USER_GROUP_STUDENTS,
  USER_GROUP_TEACHERS,
} from '@/constants/appConstants'
import { LS_IMPERSONATOR_KEY, LS_USER_KEY } from '@/constants/practiceConstants'
import store from '@/store'
import { queryUserIsAuthenticated } from '@/store/queryHelpers'
import { cloneRaw } from '@/util/util'

import logoBDark from '../assets/images/logo-b-dark.png'
import logoBLight from '../assets/images/logo-b-light.png'
import logoBarDark from '../assets/images/logo-bar-dark.png'
import logoBarLight from '../assets/images/logo-bar-light.png'
import logoBatDark from '../assets/images/logo-bat-dark.png'
import logoBatLight from '../assets/images/logo-bat-light.png'

interface UpdateUserParameters {
  user: UserGrapheneType
  torahMelodyIDs: Array<number>
  haftarahMelodyIDs: Array<number>
  sentenceGroupMelodyIDs: Array<number>
  pronunciationIDs: Array<number>
  tags: Array<string>
}

const useUtil = (logger?: VueLogger) => {
  const PUBLIC_USER = PUBLIC_USERNAME

  type GenderSiteType = {
    host: string
    mitzvah: string
    title: string
  }

  type GenderMapType = {
    [key: string]: GenderSiteType
  }

  const GENDER_MAP: GenderMapType = {
    b: {
      host: 'b-mitzvahtrainer.com',
      mitzvah: 'B-Mitzvah',
      title: 'B-Mitzvah Trainer',
    },

    bar: {
      host: 'barmitzvahtrainer.com',
      mitzvah: 'Bar Mitzvah',
      title: 'Bar Mitzvah Trainer',
    },

    bat: {
      host: 'batmitzvahtrainer.com',
      mitzvah: 'Bat Mitzvah',
      title: 'Bat Mitzvah Trainer',
    },
  }

  type LogoType = {
    light: string
    dark: string
  }

  type FallbackEntry = {
    logo: LogoType
    gender: string
    environment: string
  }

  type GenderFallbackType = {
    [ip: string]: FallbackEntry
  }

  const GENDER_FALLBACK: GenderFallbackType = {
    '172.16.126.1': {
      environment: ENV_DEVELOPMENT,
      gender: 'bar',
      logo: {
        dark: logoBarDark,
        light: logoBarLight,
      },
    },

    '192.168.1.5': {
      environment: ENV_DEVELOPMENT,
      gender: 'bar',
      logo: {
        dark: logoBarDark,
        light: logoBarLight,
      },
    },

    'b-mitzvahtrainer.com': {
      environment: ENV_PRODUCTION,
      gender: 'b',
      logo: {
        dark: logoBDark,
        light: logoBLight,
      },
    },

    'barmitzvahtrainer.com': {
      environment: ENV_PRODUCTION,
      gender: 'bar',
      logo: {
        dark: logoBarDark,
        light: logoBarLight,
      },
    },

    'batmitzvahtrainer.com': {
      environment: ENV_PRODUCTION,
      gender: 'bat',
      logo: {
        dark: logoBatDark,
        light: logoBatLight,
      },
    },

    localhost: {
      environment: ENV_DEVELOPMENT,
      gender: 'bar',
      logo: {
        dark: logoBarDark,
        light: logoBarLight,
      },
    },

    'staging.b-mitzvahtrainer.com': {
      environment: ENV_STAGING,
      gender: 'b',
      logo: {
        dark: logoBDark,
        light: logoBLight,
      },
    },

    'staging.barmitzvahtrainer.com': {
      environment: ENV_STAGING,
      gender: 'bar',
      logo: {
        dark: logoBarDark,
        light: logoBarLight,
      },
    },

    'staging.batmitzvahtrainer.com': {
      environment: ENV_STAGING,
      gender: 'bat',
      logo: {
        dark: logoBatDark,
        light: logoBatLight,
      },
    },
  }

  function userGroups(): Array<string> {
    return [...store.getters.user.userGroupNames]
  }

  function hostnameEntry(): FallbackEntry {
    return GENDER_FALLBACK[window.location.hostname] ?? GENDER_FALLBACK['localhost']
  }

  function environment(): string {
    return hostnameEntry().environment
  }

  function isProduction(): boolean {
    return environment() === ENV_PRODUCTION
  }

  function isStaging(): boolean {
    return environment() === ENV_STAGING
  }

  function isDevelopment(): boolean {
    return environment() === ENV_DEVELOPMENT
  }

  function isStaff(): boolean {
    return store.getters.user.userIsStaff
  }

  function isReadOnly(): boolean {
    return store.getters.user.userIsReadOnly
  }

  function isTeacher(): boolean {
    return userGroups().includes(USER_GROUP_TEACHERS)
  }

  function isStudent(): boolean {
    return userGroups().includes(USER_GROUP_STUDENTS)
  }

  function userGender(): string {
    return store.getters.user.userGender || hostnameEntry().gender
  }

  function userMitzvah(): string {
    return GENDER_MAP[userGender()].mitzvah
  }

  function userTrainer(): string {
    return GENDER_MAP[userGender()].title
  }

  function userHostname(): string {
    return GENDER_MAP[userGender()].host
  }

  function resetStoreExceptUser(): void {
    store.dispatch.admin.reset()
    store.dispatch.audio.reset()
    // store.dispatch.dialog.reset()
    // store.dispatch.snackbar.reset()
    store.dispatch.practiceRecordings.reset()
    store.dispatch.staff.reset()
    store.dispatch.student.reset()
    store.dispatch.style.reset()
    store.dispatch.teacher.reset()
  }

  /**
   * Clear persisted state, tell the backend to end the session, and hard-reload to /login.
   * A full reload kills all in-flight GraphQL queries and resets all Vuex state,
   * eliminating race conditions from pending responses re-populating the store.
   */
  async function forceLogout(): Promise<void> {
    localStorage.removeItem(LS_USER_KEY)
    localStorage.removeItem(LS_IMPERSONATOR_KEY)
    // wait for the backend, otherwise the reload cancels the logout request
    await store.dispatch.user.logout()
    window.location.replace(ROUTE_LOGIN)
  }

  function loginPublicUser(router: Router): void {
    const pw = generator.generate({ length: 12, numbers: true })
    store.dispatch.user.login({ password: pw, username: PUBLIC_USER }).then((ok) => {
      if (ok) {
        router.push(ROUTE_PRACTICE)
      }
    })
  }

  const browserCheck = () => {
    const { isChrome, isEdge, isFirefox } = useBrowserDetect()
    if (!isFirefox && !isEdge && !isChrome) {
      store.dispatch.dialog.show({
        message: 'This browser is not supported; please use Chrome, Edge, or Firefox.',
        title: 'Browser not supported',
      })
    }
  }

  const loginCheck = () => {
    queryUserIsAuthenticated()
      .then((isAuthenticated) => {
        if (!isAuthenticated) {
          console.log('forcing logout because userIsAuthenticated.ok is false')
          forceLogout()
        }
      })
      .catch(() => {
        logger && logger.debug('forcing logout because user is not logged in')
        forceLogout()
      })
  }

  const selectableUsers = computed<Array<string>>(() => {
    if (!store.getters.user.user) {
      return []
    }

    const users: string[] = [store.getters.user.userUsername]
    if (isStaff()) {
      users.push(ROOT_USERNAME)
    }
    return users
  })

  const getCsrfToken = () => {
    const cookies = document.cookie.split(';')
    const csrfTokenCookie = cookies.find((cookie) => cookie.trim().startsWith(`${CSRF_COOKIE_NAME}=`))
    if (!csrfTokenCookie) {
      return null
    }
    return csrfTokenCookie.split('=')[1]
  }

  const stopImpersonation = async (router: Router) => {
    const response = await fetch(BACKEND_IMPERSONATE_STOP_URL, {
      credentials: 'include',
      headers: {
        [CSRF_HEADER_NAME]: getCsrfToken() ?? '',
      },
      method: 'POST',
    })

    if (response.ok) {
      const user = cloneRaw(store.getters.user.impersonator)
      resetStoreExceptUser()
      store.dispatch.user.sanitize()
      store.dispatch.user.clearImpersonator()
      store.dispatch.user.setUser(user as UserGrapheneType)
      router.push(store.getters.user.impersonatorUrl)
    }
  }

  return {
    GENDER_MAP,
    browserCheck,
    environment,
    forceLogout,
    getCsrfToken,
    isDevelopment,
    isProduction,
    isReadOnly,
    isStaff,
    isStaging,
    isStudent,
    isTeacher,
    loginCheck,
    loginPublicUser,
    resetStoreExceptUser,
    selectableUsers,
    stopImpersonation,
    userGender,
    userHostname,
    userMitzvah,
    userTrainer,
  }
}

export { UpdateUserParameters, useUtil }
